import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from 'lucide-react';
import { CustomRSVPQuestion, ConditionalLogic } from './CustomRSVPBuilder';
import { toast } from 'sonner';

interface ConditionalLogicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question: CustomRSVPQuestion;
  allQuestions: CustomRSVPQuestion[];
  onSave: (logic: ConditionalLogic[]) => void;
}

export function ConditionalLogicDialog({ 
  open, 
  onOpenChange, 
  question, 
  allQuestions, 
  onSave 
}: ConditionalLogicDialogProps) {
  const [rules, setRules] = useState<ConditionalLogic[]>(question.conditionalLogic || []);

  const availableTriggerQuestions = allQuestions.filter(q => 
    q.id !== question.id && 
    ['select', 'radio', 'checkbox', 'multiselect'].includes(q.type) &&
    q.options && q.options.length > 0
  );

  const availableTargetQuestions = allQuestions.filter(q => q.id !== question.id);

  const addRule = () => {
    const newRule: ConditionalLogic = {
      id: `rule_${Date.now()}`,
      triggerQuestionId: '',
      triggerValue: '',
      action: 'show',
      targetQuestionIds: []
    };
    setRules([...rules, newRule]);
  };

  const updateRule = (ruleIndex: number, updates: Partial<ConditionalLogic>) => {
    setRules(rules.map((rule, index) => 
      index === ruleIndex ? { ...rule, ...updates } : rule
    ));
  };

  const removeRule = (ruleIndex: number) => {
    setRules(rules.filter((_, index) => index !== ruleIndex));
  };

  const handleSave = () => {
    const validRules = rules.filter(rule => 
      rule.triggerQuestionId && 
      rule.triggerValue && 
      rule.targetQuestionIds.length > 0
    );
    
    onSave(validRules);
    onOpenChange(false);
    toast.success('Conditional logic saved successfully');
  };

  const getTriggerQuestionOptions = (triggerQuestionId: string) => {
    const triggerQuestion = availableTriggerQuestions.find(q => q.id === triggerQuestionId);
    return triggerQuestion?.options || [];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Conditional Logic for: {question.text}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="text-sm text-muted-foreground">
            Set up rules to show/hide or modify other questions based on how this question is answered.
          </div>

          {rules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No conditional logic rules yet. Click "Add Rule" to create your first rule.
            </div>
          ) : (
            <div className="space-y-4">
              {rules.map((rule, ruleIndex) => (
                <div key={rule.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Rule {ruleIndex + 1}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRule(ruleIndex)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>When this question</Label>
                      <Select
                        value={rule.triggerQuestionId}
                        onValueChange={(value) => updateRule(ruleIndex, { 
                          triggerQuestionId: value,
                          triggerValue: '' // Reset trigger value when question changes
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select trigger question" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTriggerQuestions.map((q) => (
                            <SelectItem key={q.id} value={q.id}>
                              {q.text}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Has this value</Label>
                      <Select
                        value={rule.triggerValue}
                        onValueChange={(value) => updateRule(ruleIndex, { triggerValue: value })}
                        disabled={!rule.triggerQuestionId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select trigger value" />
                        </SelectTrigger>
                        <SelectContent>
                          {getTriggerQuestionOptions(rule.triggerQuestionId).map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Then</Label>
                      <Select
                        value={rule.action}
                        onValueChange={(value: any) => updateRule(ruleIndex, { action: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="show">Show</SelectItem>
                          <SelectItem value="hide">Hide</SelectItem>
                          <SelectItem value="required">Make Required</SelectItem>
                          <SelectItem value="skip">Skip</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>These questions</Label>
                      <Select
                        onValueChange={(value) => {
                          const currentTargets = rule.targetQuestionIds || [];
                          if (!currentTargets.includes(value)) {
                            updateRule(ruleIndex, { 
                              targetQuestionIds: [...currentTargets, value] 
                            });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select target questions" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTargetQuestions.map((q) => (
                            <SelectItem key={q.id} value={q.id}>
                              {q.text}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {rule.targetQuestionIds && rule.targetQuestionIds.length > 0 && (
                    <div className="space-y-2">
                      <Label>Selected target questions:</Label>
                      <div className="flex flex-wrap gap-2">
                        {rule.targetQuestionIds.map((targetId) => {
                          const targetQuestion = availableTargetQuestions.find(q => q.id === targetId);
                          return (
                            <Badge 
                              key={targetId} 
                              variant="secondary"
                              className="cursor-pointer"
                              onClick={() => updateRule(ruleIndex, {
                                targetQuestionIds: rule.targetQuestionIds.filter(id => id !== targetId)
                              })}
                            >
                              {targetQuestion?.text}
                              <span className="ml-1">×</span>
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={addRule}>
              <Plus className="h-4 w-4 mr-2" />
              Add Rule
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save Logic
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}